import { Badge } from "ui/components/badge";
import { Link } from "react-router-dom";
import React from "react";
import { DisplayAmount } from "components/display/amount";
import { usePrivacy } from "contexts/privacy";
import { maskPrivacyAmount } from "utils/utils";
import { useCurrencies } from "@/modules/reports/contexts/currency-context";
import { ROUTE } from "@/lib/utils";

const getUniqueDepartments = (jobs) => {
  const departments = new Set();
  jobs?.forEach((job) => {
    job.employees?.forEach((emp) => {
      if (emp.employee?.department?.name) {
        departments.add(emp.employee.department.name);
      }
    });
  });
  return Array.from(departments);
};

export const ProjectCell = ({ row }) => (
  <Link
    to={ROUTE.PROJECT(row.original.project_id)}
    className="hover:underline"
    target="_blank"
  >
    {row.original.name}
  </Link>
);

export const ClientCell = ({ row }) => (
  <Link to={`/clients/${row.original.client?.company_id?.id}`}>
    {row.original.client?.company_id?.name}
  </Link>
);

export const DepartmentsCell = ({ row }) => {
  const departments = getUniqueDepartments(row.original.jobs);
  return (
    <div className="flex gap-2 flex-wrap">
      {departments.map((dept) => (
        <Badge key={dept} variant="outline">
          {dept}
        </Badge>
      ))}
    </div>
  );
};

export const ActiveJobsCell = ({ getValue }) => {
  const jobs = getValue();

  return jobs?.filter((job) => job.status === "in_progress")?.length || 0;
};

export const ViewAmountCell = ({ getValue }) => {
  const jobs = getValue();
  return <AmountCell jobs={jobs} />;
};

// Reusable function to calculate total billable amount in INR
const calculateTotalBillableAmountInINR = (jobs, convertToINR) => {
  if (!jobs || !Array.isArray(jobs)) return 0;

  return jobs.reduce((total, job) => {
    if (!job.billables || !Array.isArray(job.billables)) return total;

    const jobTotal = job.billables.reduce((jobSum, billable) => {
      const amount = billable.adjusted_amount || 0;
      const convertedAmount = convertToINR(amount, billable.currency);
      return jobSum + convertedAmount;
    }, 0);

    return total + jobTotal;
  }, 0);
};

// Component for rendering amount with privacy support
export const AmountCell = ({ jobs }) => {
  const { convertToINR } = useCurrencies();

  const totalAmount = calculateTotalBillableAmountInINR(jobs, convertToINR);

  return <DisplayAmount value={totalAmount} />;
};

// Generic function to calculate billing percentage by job type
const calculateBillingPercentageByType = (jobs, convertToINR, targetType) => {
  if (!jobs || !Array.isArray(jobs)) return 0;

  let totalAmount = 0;
  let typeAmount = 0;

  jobs.forEach((job) => {
    if (!job.billables || !Array.isArray(job.billables)) return;

    job.billables.forEach((billable) => {
      // Prioritize adjusted_amount over amount
      const amount = billable.adjusted_amount || billable.amount || 0;
      const convertedAmount = convertToINR(amount, billable.currency);

      totalAmount += convertedAmount;

      // Check if this job matches the target type
      if (job.type === targetType) {
        typeAmount += convertedAmount;
      }
    });
  });

  if (totalAmount === 0) return 0;
  return (typeAmount / totalAmount) * 100;
};

// Function to calculate dedicated billing percentage
const calculateDedicatedBillingPercentage = (jobs, convertToINR) => {
  return calculateBillingPercentageByType(jobs, convertToINR, "dedicated");
};

// Function to calculate fixed billing percentage
const calculateFixedBillingPercentage = (jobs, convertToINR) => {
  return calculateBillingPercentageByType(jobs, convertToINR, "fixed");
};

// Function to calculate hourly billing percentage
const calculateHourlyBillingPercentage = (jobs, convertToINR) => {
  return calculateBillingPercentageByType(jobs, convertToINR, "hourly");
};

// Function to calculate bucket billing percentage
const calculateBucketBillingPercentage = (jobs, convertToINR) => {
  return calculateBillingPercentageByType(jobs, convertToINR, "bucket");
};

// Function to calculate recurring billing percentage
const calculateRecurringBillingPercentage = (jobs, convertToINR) => {
  return calculateBillingPercentageByType(jobs, convertToINR, "recurring");
};

// Component for rendering dedicated billing percentage
export const DedicatedBillingPercentageCell = ({ getValue }) => {
  const { convertToINR } = useCurrencies();
  const jobs = getValue();
  const percentage = calculateDedicatedBillingPercentage(jobs, convertToINR);

  // console.log(
  //   jobs?.flatMap((job) => job.billables.map((billable) => billable.type))
  // );

  return (
    <div className="text-right">
      <span className="font-medium">{percentage.toFixed(1)}%</span>
    </div>
  );
};

// Component for rendering fixed billing percentage
export const FixedBillingPercentageCell = ({ getValue }) => {
  const { convertToINR } = useCurrencies();
  const jobs = getValue();
  const percentage = calculateFixedBillingPercentage(jobs, convertToINR);

  return (
    <div className="text-right">
      <span className="font-medium">{percentage.toFixed(1)}%</span>
    </div>
  );
};

// Component for rendering hourly billing percentage
export const HourlyBillingPercentageCell = ({ getValue }) => {
  const { convertToINR } = useCurrencies();
  const jobs = getValue();
  const percentage = calculateHourlyBillingPercentage(jobs, convertToINR);

  return (
    <div className="text-right">
      <span className="font-medium">{percentage.toFixed(1)}%</span>
    </div>
  );
};

// Component for rendering hourly billing percentage
export const BucketBillingPercentageCell = ({ getValue }) => {
  const { convertToINR } = useCurrencies();
  const jobs = getValue();
  const percentage = calculateBucketBillingPercentage(jobs, convertToINR);

  return (
    <div className="text-right">
      <span className="font-medium">{percentage.toFixed(1)}%</span>
    </div>
  );
};

// Component for rendering hourly billing percentage
export const RecurringBillingPercentageCell = ({ getValue }) => {
  const { convertToINR } = useCurrencies();
  const jobs = getValue();
  const percentage = calculateRecurringBillingPercentage(jobs, convertToINR);

  return (
    <div className="text-right">
      <span className="font-medium">{percentage.toFixed(1)}%</span>
    </div>
  );
};
